import * as React from 'react';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import NotificationProps from './NotificationProps.interface';
import './notification.scss';
import { ResponseError } from '../API/ResponseError';
import { Logging, hashString } from '../..';

const refreshIntervalMilliseconds = 10;

const Notification = ({
  data,
  position = 'Center',
  autoClose = false,
  autoCloseTimeOutMilliseconds = 2000,
  hideProgress = false,
  hideClose = false,
  onNotificationClose,
}: NotificationProps): JSX.Element => {
  // Notification Data is not set.  Dont show anything
  if (!data) {
    return (<></>);
  }

  // If the Notification Data value is specified, it overrides the Notfication value
  const localAutoCloseTimeOutMilliseconds = data.autoCloseTimeOutMilliseconds === undefined
    ? autoCloseTimeOutMilliseconds
    : data.autoCloseTimeOutMilliseconds;
  const localAutoClose = data.autoClose === undefined ? autoClose : data.autoClose;
  const localHideProgress: boolean = data.hideProgress === undefined ? hideProgress : data.hideProgress;
  const localHideClose: boolean = data.hideClose === undefined ? hideClose : data.hideClose;
  const localPosition = data.position === undefined ? position : data.position;

  const [update, setUpdate] = React.useState(0);
  const [numberUpdates] = React.useState(localAutoCloseTimeOutMilliseconds / refreshIntervalMilliseconds);
  const [title] = React.useState(data.title);
  const [error] = React.useState(data.error);
  const [appendMessagesForStatusCodes] = React.useState(data.appendMessagesForStatusCodes);
  const [hideStatusCodes] = React.useState(data.hideStatusCodes);

  // Append the Inner Error Message if Needed
  const getDescription = (descriptions: string | string[]) : string | string[] => {
    if (error) {
      // If the Status Code of Error matches, append the Error->Message from the Response
      if (appendMessagesForStatusCodes && (appendMessagesForStatusCodes.indexOf(error.statusCode) !== -1)) {
        // Append to the Existing Array
        if (Array.isArray(descriptions)) {
          return descriptions.concat(error.message);
        }

        // Create a new Array
        return [descriptions, error.message];
      }
    }

    return descriptions;
  };

  const [description] = React.useState(getDescription(data.description));

  // Close the Side By Side Viewer
  const onClose = (): void => {
    onNotificationClose();
  };

  const renderDescription = (): JSX.Element | JSX.Element[] => {
    if (description) {
      if (Array.isArray(description)) {
        // eslint-disable-next-line arrow-body-style
        return description.map((descriptionItem: string) => {
          const key: string = hashString(descriptionItem);
          return (<div key={key}><span>{descriptionItem}</span><br /></div>);
        });
      }

      return (<span>{description}</span>);
    }

    return null;
  };

  const renderError = (responseError: ResponseError): JSX.Element => {
    if (responseError) {
      // Hide Specific Error Codes if needed
      if (hideStatusCodes && (hideStatusCodes.indexOf(error.statusCode) !== -1)) {
        return null;
      }

      const errorMessage: string = responseError.statusCodeDescription;

      // Show the Error Message in the Tooltip
      return (<span title={error.message}>{errorMessage}</span>);
    }

    return null;
  };

  const getNoficationIcon = (): string => {
    switch (data.type) {
      case 'Information':
        return 'notification-information';
      case 'Success':
        return 'notification-success';
      case 'Warning':
        return 'notification-warning';
      case 'Error':
        return 'notification-error';
      default:
        return 'notification-error';
    }
  };

  React.useEffect(() => {
    let timer: NodeJS.Timeout = null;

    if (data && localAutoClose && localAutoCloseTimeOutMilliseconds) {
      if (update >= numberUpdates) {
        // Auto Close the Notification
        onClose();
      } else {
        timer = setInterval(() => {
          setUpdate(update + 1);
        }, refreshIntervalMilliseconds);
      }
    }
    return (): void => clearTimeout(timer);
  }, [update]);

  const progress = numberUpdates ? (update / numberUpdates) * 100 : 0;

  const progressStyle = {
    width: `${progress}%`,
  };

  const overlayStyle = `notificationOverlay notificationOverlay${localPosition}`;
  const notificationIcon = `/images/${getNoficationIcon()}.png`;
  const notificationProgressClass = `notificationProgress${data.type}`;

  Logging.Debug('Notification', localPosition);

  return (
    <div className={overlayStyle}>
      <div className="notificationContainer">
        <div className="notificationTitleContainer">
          <div className="notificationIcon">
            <img src={notificationIcon} alt="Notification" className="notificationIcon" />
          </div>
          <div className="notificationTitle">
            <span>{title}</span>
          </div>
          {
            !localHideClose
            && (
              <div className="notificationClose">
                <VtxButton
                  id="notificationCloseButton"
                  type="link"
                  onClick={(): void => onClose()}
                  icon={<VtxIcon type="close-small" fontSize={12} />}
                />
              </div>
            )
          }
        </div>
        <div className="notificationDescription">
          {renderDescription()}
          <div className="notificationError">
            {renderError(error)}
          </div>
        </div>
        {
          localAutoClose && localAutoCloseTimeOutMilliseconds && !localHideProgress
            ? (
              <div className="notificationProgressContainer">
                <div className={`notificationProgressBar ${notificationProgressClass}`} style={progressStyle} />
              </div>
            )
            : (
              <div className="notificationProgressPlaceHolder" />
            )
        }
      </div>
    </div>
  );
};

export default Notification;
